import qs, { IParseOptions } from 'qs';
import { Path } from '@remix-run/router';
import omitBy from 'lodash/omitBy';
import moment from 'moment';

import history from '@shared/utils/history';
import { Queries } from '@shared/types/common';

export const getQueries = (options?: IParseOptions): Queries =>
  qs.parse(history.location.search, { ignoreQueryPrefix: true, arrayLimit: Infinity, ...options });

export const Format_MMDDYYYY = 'MM/DD/YYYY';
export const Format_HMM = 'h:mm A';

export const setQueries = (queries: Queries, replace?: boolean, locationArgs?: Partial<Path>) => {
  const args = {
    search: qs.stringify(
      omitBy(queries, value => !value),
      { addQueryPrefix: true },
    ),
    ...locationArgs,
  };

  if (replace) {
    return history.replace(args);
  }

  return history.push(args);
};

export const deleteQueries = (
  queriesToDelete?: Array<string>,
  replace?: boolean,
  locationArgs?: Partial<Path>,
) => {
  if (!queriesToDelete) {
    return setQueries({}, replace, locationArgs);
  }

  const currentQueries = getQueries();
  const finalQueries = omitBy(currentQueries, (value, key) => queriesToDelete.includes(key));

  setQueries(finalQueries, replace, locationArgs);
};

export const getTestAttr = (module: string, elementDescription: string, elementName: string) => ({
  'data-test-id': `${module}-${elementDescription}-${elementName}`,
});

export const formatDate = (
  date: string | Date | undefined,
  format: string = 'MMM DD, YYYY',
  utc: boolean = true,
) => {
  if (!date) {
    return '';
  }

  if (utc) {
    return moment.utc(date).format(format);
  }

  return moment(date).local().format(format);
};

export const formatDateInTimeZone = (
  date: string | Date | undefined,
  format: string = 'MMM DD, YYYY',
  timeZoneOffset?: string,
) => {
  if (!date) {
    return '';
  }

  if (!timeZoneOffset) {
    return moment.utc(date).format(format);
  }

  return moment.utc(date).utcOffset(timeZoneOffset).format(format);
};

export const toLowerCaseFirst = (str: string) => str.charAt(0).toLowerCase() + str.substr(1);

export const tryExtractTimezoneOffset = (timeZone?: string) => {
  if (!timeZone) {
    return '';
  }

  const regex = /^([+-]?\d{2}):/;
  const match = timeZone.match(regex);

  if (match) {
    return Number(match[1]) >= 0 ? `+${Number(match[1])}` : Number(match[1]).toString();
  }

  return timeZone;
};
